// Dashboard page (ADR-181 P1): the operator's landing view combining D2's
// license-health alerts with the non-IBM slice of D1's revenue overview,
// plus the D5 rider's connectivity/deployment_type badge and expiry
// countdown on every alert card -- one screen answering both "who needs
// attention right now" and "is the business healthy," per the ADR's
// recommended P1 phasing and its rationale for building them as a single
// landing page rather than sequencing them separately.
//
// Both RPCs backing this page (ListLicenseHealthAlerts, GetRevenueOverview)
// are OPERATOR-ONLY, cross-account aggregations -- see
// services/console/server/dashboard.go and proto/console.proto's
// "Back-office dashboard" RPC block. This page renders inside the SAME
// operator shell (RootLayout) every other console route uses; it is never
// reachable from a customer-facing surface (ADR-179's separation, which
// this ADR reaffirms rather than weakens).
//
// Three-surface UI alignment pass (three-surface-ui-alignment): restyled
// onto Genesis tokens + the ui/ app's glass-card idiom -- 12px card
// radius, JetBrains Mono for IDs/metrics, status colors from the Genesis
// status table instead of ad-hoc amber/red/orange Tailwind classes. Data
// layer (queries, formatters) untouched.
import { useQuery } from '@tanstack/react-query'
import * as pb from '@/generated/console_pb'
import { getRevenueOverview, listLicenseHealthAlerts } from '@/lib/grpcweb/console-api'
import {
  billingStateLabel,
  connectivityBadgeLabel,
  formatExpiryCountdown,
  licenseStatusLabel,
  licenseTierLabel,
} from '@/lib/formatters'
import { DANGER, DARK, GREY_200, GREY_300, GREY_400, GREY_500, INFO, WARNING, WHITE, errorBlockStyle } from '@/lib/genesis'

const EXPIRING_WITHIN_DAYS = 14

const TONE_COLOR: Record<'amber' | 'red' | 'orange', string> = {
  amber: WARNING,
  red: DANGER,
  orange: INFO,
}

function AlertCard({
  title,
  count,
  tone,
  children,
}: {
  title: string
  count: number
  tone: 'amber' | 'red' | 'orange'
  children: React.ReactNode
}) {
  const accent = TONE_COLOR[tone]
  return (
    <div className="glass-card p-5" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: GREY_300 }}>{title}</h3>
        <span className="font-mono text-2xl font-semibold" style={{ color: accent }}>{count}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  )
}

function ConnectivityBadge({ deploymentType, isAirGapped }: { deploymentType: pb.DeploymentType; isAirGapped: boolean }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
      style={{
        border: `1px solid ${isAirGapped ? WARNING : GREY_500}`,
        color: isAirGapped ? WARNING : GREY_400,
      }}
      title={isAirGapped ? 'No remote kill-switch -- offline instances only ever expire' : undefined}
    >
      {connectivityBadgeLabel(deploymentType)}
    </span>
  )
}

function LicenseAlertRow({ alert, showExpiry }: { alert: pb.LicenseHealthAlert.AsObject; showExpiry: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'rgba(0, 0, 0, 0.25)' }}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium" style={{ color: WHITE }}>{alert.accountName || alert.accountId}</span>
          <ConnectivityBadge deploymentType={alert.deploymentType} isAirGapped={alert.isAirGapped} />
        </div>
        <div className="mt-0.5 text-xs" style={{ color: GREY_400 }}>
          {licenseTierLabel(alert.tier)} &middot; {licenseStatusLabel(alert.status)}
          {alert.isAirGapped && (
            <span className="ml-1" style={{ color: INFO }}>
              &middot; {alert.status === pb.LicenseStatus.LICENSE_STATUS_SUSPENDED ? 're-issue to resume' : 're-issue or let lapse'}
            </span>
          )}
        </div>
      </div>
      {showExpiry && (
        <div className="ml-3 shrink-0 text-right font-mono text-xs" style={{ color: GREY_300 }}>
          {formatExpiryCountdown(alert.daysUntilExpiry, !!alert.expiresAt)}
        </div>
      )}
    </div>
  )
}

// OrgScopeAlertRow (ADR-185 Phase B D7/D8): current_org_count is an
// INVENTORY-ONLY signal (D9) -- it counts distinct orgs Console has heard
// FROM, not a usage-volume metric. The "X / Y orgs" label mirrors the seat
// panel's "X / Y seats" shape deliberately, but its title text below spells
// out the inventory-only caveat once at the section level rather than
// repeating it per row.
function OrgScopeAlertRow({ alert }: { alert: pb.OrgScopeAlert.AsObject }) {
  return (
    <div className="flex items-center justify-between rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'rgba(0, 0, 0, 0.25)' }}>
      <div className="min-w-0 flex-1">
        <span className="truncate font-medium" style={{ color: WHITE }}>{alert.accountName || alert.accountId}</span>
        {alert.lastNewOrgSeenAt && (
          <div className="mt-0.5 text-xs" style={{ color: GREY_400 }}>
            last new org seen {new Date(alert.lastNewOrgSeenAt.seconds * 1000).toLocaleDateString()}
          </div>
        )}
      </div>
      <div className="ml-3 shrink-0 text-right font-mono text-xs font-medium" style={{ color: INFO }}>
        {alert.currentOrgCount} / {alert.orgLimit} orgs
      </div>
    </div>
  )
}

// StaleHeartbeatAlertRow (ADR-185 Phase B D8): a license whose instance has
// gone quiet -- distinct from `suspended`, this can fire on a perfectly
// active/unsuspended license whose instance simply stopped phoning home.
function StaleHeartbeatAlertRow({ alert }: { alert: pb.StaleHeartbeatAlert.AsObject }) {
  const neverHandshook = !alert.lastHandshakeAt
  return (
    <div className="flex items-center justify-between rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'rgba(0, 0, 0, 0.25)' }}>
      <div className="min-w-0 flex-1">
        <span className="truncate font-medium" style={{ color: WHITE }}>{alert.accountName || alert.accountId}</span>
        <div className="mt-0.5 truncate text-xs" style={{ color: GREY_400 }}>{alert.instanceId}</div>
      </div>
      <div className="ml-3 shrink-0 text-right font-mono text-xs" style={{ color: GREY_300 }}>
        {neverHandshook ? 'never handshook' : `${alert.hoursSinceHandshake}h since handshake`}
      </div>
    </div>
  )
}

function SeatOverLimitRow({ alert }: { alert: pb.LicenseHealthAlert.AsObject }) {
  return (
    <div className="flex items-center justify-between rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: 'rgba(0, 0, 0, 0.25)' }}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium" style={{ color: WHITE }}>{alert.accountName || alert.accountId}</span>
          <ConnectivityBadge deploymentType={alert.deploymentType} isAirGapped={alert.isAirGapped} />
        </div>
        <div className="mt-0.5 text-xs" style={{ color: GREY_400 }}>{licenseTierLabel(alert.tier)}</div>
      </div>
      <div className="ml-3 shrink-0 text-right font-mono text-xs font-medium" style={{ color: INFO }}>
        {alert.activeSeatCount} / {alert.seatLimit} seats
      </div>
    </div>
  )
}

export function DashboardPage() {
  const {
    data: healthData,
    isLoading: healthLoading,
    isError: healthError,
  } = useQuery({
    queryKey: ['license-health-alerts', EXPIRING_WITHIN_DAYS],
    queryFn: () => listLicenseHealthAlerts({ expiringWithinDays: EXPIRING_WITHIN_DAYS }),
  })

  const {
    data: revenueData,
    isLoading: revenueLoading,
    isError: revenueError,
  } = useQuery({
    queryKey: ['revenue-overview'],
    queryFn: () => getRevenueOverview(),
  })

  const health = healthData?.toObject()
  const revenue = revenueData?.toObject()

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-2xl font-semibold" style={{ color: WHITE }}>Dashboard</h1>
        <p className="mt-1 text-sm" style={{ color: GREY_400 }}>
          License health and revenue overview across every account.
        </p>
      </div>

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide" style={{ color: GREY_400 }}>
          License health
        </h2>
        {healthLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading license health…</p>}
        {healthError && (
          <div className="p-3" style={errorBlockStyle}>
            <p className="text-sm" style={{ color: WHITE }}>Failed to load license health alerts.</p>
          </div>
        )}
        {health && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <AlertCard title={`Expiring within ${EXPIRING_WITHIN_DAYS} days`} count={health.expiringSoonList.length} tone="amber">
              {health.expiringSoonList.length === 0 && (
                <p className="text-xs" style={{ color: GREY_400 }}>No licenses expiring soon.</p>
              )}
              {health.expiringSoonList.map((a) => (
                <LicenseAlertRow key={a.licenseId} alert={a} showExpiry />
              ))}
            </AlertCard>

            <AlertCard title="Suspended" count={health.suspendedList.length} tone="red">
              {health.suspendedList.length === 0 && (
                <p className="text-xs" style={{ color: GREY_400 }}>No suspended licenses.</p>
              )}
              {health.suspendedList.map((a) => (
                <LicenseAlertRow key={a.licenseId} alert={a} showExpiry={false} />
              ))}
            </AlertCard>

            <AlertCard title="Seat over limit" count={health.seatOverLimitList.length} tone="orange">
              {health.seatOverLimitList.length === 0 && (
                <p className="text-xs" style={{ color: GREY_400 }}>No licenses over their seat limit.</p>
              )}
              {health.seatOverLimitList.map((a) => (
                <SeatOverLimitRow key={a.licenseId} alert={a} />
              ))}
            </AlertCard>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide" style={{ color: GREY_400 }}>
          Org federation (ADR-185 Phase B)
        </h2>
        {health && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <AlertCard title="Over org scope" count={health.orgScopeAlertsList.length} tone="orange">
              <p className="mb-1 text-xs" style={{ color: GREY_500 }}>
                Org counts are inventory-only (which orgs Console has heard from), not a usage-volume metric.
              </p>
              {health.orgScopeAlertsList.length === 0 && (
                <p className="text-xs" style={{ color: GREY_400 }}>No account-bound licenses over their org limit.</p>
              )}
              {health.orgScopeAlertsList.map((a) => (
                <OrgScopeAlertRow key={a.licenseId} alert={a} />
              ))}
            </AlertCard>

            <AlertCard title="Stale heartbeat" count={health.staleHeartbeatAlertsList.length} tone="red">
              {health.staleHeartbeatAlertsList.length === 0 && (
                <p className="text-xs" style={{ color: GREY_400 }}>Every instance has handshook recently.</p>
              )}
              {health.staleHeartbeatAlertsList.map((a) => (
                <StaleHeartbeatAlertRow key={a.licenseId} alert={a} />
              ))}
            </AlertCard>
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide" style={{ color: GREY_400 }}>
          Revenue overview
        </h2>
        {revenueLoading && <p className="text-sm" style={{ color: GREY_400 }}>Loading revenue overview…</p>}
        {revenueError && (
          <div className="p-3" style={errorBlockStyle}>
            <p className="text-sm" style={{ color: WHITE }}>Failed to load revenue overview.</p>
          </div>
        )}
        {revenue && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="glass-card p-4">
                <div className="text-xs uppercase tracking-wide" style={{ color: GREY_400 }}>Accounts</div>
                <div className="mt-1 font-mono text-2xl font-semibold" style={{ color: WHITE }}>{revenue.totalAccounts}</div>
              </div>
              <div className="glass-card p-4">
                <div className="text-xs uppercase tracking-wide" style={{ color: GREY_400 }}>Active licenses</div>
                <div className="mt-1 font-mono text-2xl font-semibold" style={{ color: WHITE }}>{revenue.totalLicenses}</div>
              </div>
              <div className="glass-card p-4">
                <div className="text-xs uppercase tracking-wide" style={{ color: GREY_400 }}>Total seats</div>
                <div className="mt-1 font-mono text-2xl font-semibold" style={{ color: WHITE }}>{revenue.totalSeatLimit}</div>
              </div>
            </div>

            <div className="glass-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide" style={{ borderBottom: `1px solid ${GREY_500}`, color: GREY_400 }}>
                    <th className="px-4 py-2">Tier</th>
                    <th className="px-4 py-2">Billing state</th>
                    <th className="px-4 py-2 text-right">Licenses</th>
                    <th className="px-4 py-2 text-right">Seats</th>
                  </tr>
                </thead>
                <tbody>
                  {revenue.byTierList.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-center" style={{ color: GREY_400 }}>
                        No licenses yet.
                      </td>
                    </tr>
                  )}
                  {revenue.byTierList.map((b, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${DARK}` }}>
                      <td className="px-4 py-2" style={{ color: GREY_200 }}>{licenseTierLabel(b.tier)}</td>
                      <td className="px-4 py-2" style={{ color: GREY_400 }}>{billingStateLabel(b.billingState)}</td>
                      <td className="px-4 py-2 text-right font-mono" style={{ color: GREY_200 }}>{b.licenseCount}</td>
                      <td className="px-4 py-2 text-right font-mono" style={{ color: GREY_200 }}>{b.totalSeatLimit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-xs" style={{ color: GREY_500 }}>
              IBM-vs-direct billing channel split not yet available -- gated on ADR-180 D0's
              billing_channel column.
            </p>
          </div>
        )}
      </section>
    </div>
  )
}
